/* Generic renewable policies selection page - shows ALL renewable policy types */

import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import SkeletonLoader from "../../../components/common/Loader/SkeletonLoader";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import { PROMO_CODE } from "../../../components/newMotorPolicy/veiwSummaryPopup/viewSummaryPopup";
import PolicyCard from "../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useMotorForm from "../../../hooks/useMotorFormData";
import { RENEW_POLICY_EXPIRED, RENEW_POLICY_LOGGED_IN } from "../../../hooks/useRenewalFlow";
import { selectLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import { getCoverPrices } from "../../../redux/features/motor/getQuoteSlice";
import { motorRenewalPremium } from "../../../redux/features/motor/renewalPremiumSlice";
import { setRenewalFlowType, clearRenewalFlow } from "../../../redux/features/motorInsuranceRenewFlowSlice";
import {
  fetchUserPolicies,
  selectUserPoliciesData,
  selectUserPoliciesLoader,
} from "../../../redux/features/myPolicySlice";
import { LOCAL_STORAGE_NAME } from "../../../redux/axiosInstance";
import { clearApplyPromoState } from "../../../redux/features/promoCode/promoCodeSlice";
import { getClientSource } from "../../../utils/getClientSource";
import { screenResolution } from "../../../utils/screenResolution";
import { coverPriceRequest } from "../../motorInsurancePages/newMotorPolicy/planPages/data";
import { RENEW_POLICY_SELECTED_POLICY } from "../../motorInsurancePages/renewalMotorPolicy/selectPoliciesPage/selectPoliciesPage";
import RenewalLayout from "./RenewalLayout";
import { POLICY_TYPE_ROUTES } from "../data";

const GenericSelectPoliciesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const translate = useLanguageTranslate();
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Ref to track if component is mounted (prevents state updates after unmount)
  const isMountedRef = useRef(true);

  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const fetchPoliciesLoading = useSelector(selectUserPoliciesLoader);
  const userPolicies = useSelector(selectUserPoliciesData);

  // Motor form data hooks - use dummy setters to avoid restoring stale state from Redux
  const { saveMotorDataInRedux, removeMotorFormData } = useMotorForm(
    RENEW_POLICY_SELECTED_POLICY,
    () => {} // Dummy setter - we don't want to restore previous selection
  );
  const { removeMotorFormPromoCodeData } = useMotorForm(
    PROMO_CODE,
    () => {} // Dummy setter
  );

  // Reset state on initial mount only. We intentionally do NOT depend on
  // location.key — otherwise, when we navigate away to /renew/renew-motor-policy,
  // the location context update can cause this effect to re-fire and wipe the
  // currentStepIndex we just set to 1, landing the user back on SELECT_POLICY.
  useEffect(() => {
    isMountedRef.current = true;
    // Reset local state
    setSelected(null);
    setIsLoading(false);
    // Reset motor renewal flow Redux state to ensure clean slate
    dispatch(clearRenewalFlow());

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to extract base policy number from full policy number
  const getBasePolicyNumber = (fullPolicyNumber) => {
    const dashIndex = fullPolicyNumber.lastIndexOf("-");
    if (dashIndex !== -1) {
      const beforeDash = fullPolicyNumber.substring(0, dashIndex);
      const lastSlashIndex = beforeDash.lastIndexOf("/");
      return lastSlashIndex !== -1
        ? beforeDash.substring(lastSlashIndex + 1)
        : beforeDash;
    }
    return fullPolicyNumber;
  };

  // All product codes that have a working renewal flow.
  const supportedProductCodes = Object.keys(POLICY_TYPE_ROUTES);

  // Filter active renewable policies for all supported types.
  // Hide policies that have already been renewed (Renewed === "Yes") —
  // their successor policy is already present in the list.
  const activeRenewablePolicies = userPolicies?.filter(
    (policy) =>
      policy.IsRenewable &&
      policy.PolicyStatus === "ACTIVE POLICY" &&
      policy.Renewed !== "Yes" &&
      supportedProductCodes.includes(policy.ProductCode)
  );

  // Create a set of base policy numbers for active policies
  const activePolicyBaseNumbers = new Set(
    activeRenewablePolicies?.map((policy) =>
      getBasePolicyNumber(policy.PolicyNumber)
    ) || []
  );

  // Filter expired policies for all supported types, excluding those with active versions,
  // policies that have already been renewed, and policies the API marks as not renewable.
  const expiredPolicies = userPolicies?.filter((policy) => {
    if (
      policy.PolicyStatus !== "EXPIRED" ||
      policy.Renewed === "Yes" ||
      !policy.IsRenewable ||
      !supportedProductCodes.includes(policy.ProductCode)
    ) {
      return false;
    }

    const basePolicyNumber = getBasePolicyNumber(policy.PolicyNumber);
    return !activePolicyBaseNumbers.has(basePolicyNumber);
  });

  // Group expired policies by base policy number and get the latest one for each
  const latestExpiredPoliciesMap = expiredPolicies?.reduce((acc, current) => {
    const basePolicyNumber = getBasePolicyNumber(current.PolicyNumber);

    if (!acc[basePolicyNumber]) {
      acc[basePolicyNumber] = current;
    } else {
      const existingDate = new Date(
        acc[basePolicyNumber].EndDate || acc[basePolicyNumber].CreatedDate || 0
      );
      const currentDate = new Date(current.EndDate || current.CreatedDate || 0);

      if (currentDate > existingDate) {
        acc[basePolicyNumber] = current;
      } else if (currentDate.getTime() === existingDate.getTime()) {
        const existingSuffix =
          acc[basePolicyNumber].PolicyNumber.split("-").pop();
        const currentSuffix = current.PolicyNumber.split("-").pop();
        if (currentSuffix > existingSuffix) {
          acc[basePolicyNumber] = current;
        }
      }
    }

    return acc;
  }, {});

  const latestExpiredPolicies = latestExpiredPoliciesMap
    ? Object.values(latestExpiredPoliciesMap)
    : [];

  // Combine active renewable policies with the latest expired policies,
  // sorted by end date ascending so soonest-expiring appears first.
  const renewablePolicies = [
    ...(activeRenewablePolicies || []),
    ...latestExpiredPolicies,
  ].sort((a, b) => {
    const aTime = new Date(a?.EndDate || a?.to_date || 0).getTime();
    const bTime = new Date(b?.EndDate || b?.to_date || 0).getTime();
    if (isNaN(aTime) && isNaN(bTime)) return 0;
    if (isNaN(aTime)) return 1;
    if (isNaN(bTime)) return -1;
    return aTime - bTime;
  });

  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  useEffect(() => {
    // Always fetch fresh policies on mount so navigating back to /renew
    // always reflects the latest policy data.
    // On a hard refresh Redux state is wiped, but the auth token/user info
    // survives in localStorage. Fall back to reading CPR from there.
    let cpr = loginTokenInfo?.userName;
    if (!cpr) {
      try {
        const raw = localStorage.getItem(LOCAL_STORAGE_NAME);
        const info = raw ? JSON.parse(raw) : null;
        cpr = info?.userName || info?.Cpr || info?.cpr || null;
      } catch (e) { /* ignore storage errors */ }
    }

    if (!cpr) {
      setHasAttemptedFetch(true);
      return;
    }
    const payload = {
      Cpr: cpr,
      Channel: getClientSource(),
    };
    Promise.resolve(dispatch(fetchUserPolicies(payload))).finally(() => {
      if (isMountedRef.current) setHasAttemptedFetch(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectClickHandler = (data) => {
    // Always reset loading state when selecting a new policy
    setIsLoading(false);
    setSelected(data);
  };

  // Handle motor policy selection - calls premium API and sets up flow
  const handleMotorPolicySelection = async (policy) => {
    setIsLoading(true);

    // Check if policy is expired - skip premium API call and go directly to upload photos
    if (policy.PolicyStatus === "EXPIRED") {
      try {
        // Save selected policy data for later use after photo upload
        await saveMotorDataInRedux(policy);
        // Clear promo code
        await dispatch(clearApplyPromoState());
        await removeMotorFormPromoCodeData();
        // Change to expired policy flow - start from upload photos step (step 1)
        dispatch(setRenewalFlowType({ type: RENEW_POLICY_EXPIRED, currentStepIndex: 1, currentSubstepIndex: 0 }));
        navigate("/renew/renew-motor-policy", { state: { motorFlow: { type: RENEW_POLICY_EXPIRED, currentStepIndex: 1, currentSubstepIndex: 0 } } });
      } catch (error) {
        if (isMountedRef.current) {
          toast.error(translate("Failed to proceed with expired policy renewal", "فشل في متابعة تجديد الوثيقة المنتهية"));
          setIsLoading(false);
        }
      }
      return;
    }

    // For active policies, fetch premium + cover prices here so we land
    // directly on REVIEW_YOUR_POLICY (no flicker through motor SELECT_POLICY).
    try {
      await dispatch(clearApplyPromoState());
      await removeMotorFormPromoCodeData();

      const motorRenewalPremiumPayload = {
        PolicyNumber: policy.PolicyNumber,
        Channel: getClientSource(),
        Covers: [],
        DefaultCovers: false,
        NewPlan: policy.PlanName,
      };
      const premiumAction = await dispatch(motorRenewalPremium(motorRenewalPremiumPayload));
      const premiumResult = unwrapResult(premiumAction);

      if (!premiumResult) throw new Error("Empty premium response");

      const coverPricesPayload = coverPriceRequest(
        policy.PolicyNumber,
        policy.PolicyNumber.PlanName,
        "",
        ""
      );
      await dispatch(getCoverPrices(coverPricesPayload));

      const enrichedPolicy = {
        ...premiumResult,
        OriginalPlanName: premiumResult.PlanName,
        OriginalPolicyNo: premiumResult.QuoteDetails?.PreviousPolicyNumber,
      };
      await saveMotorDataInRedux(enrichedPolicy);

      // Land directly on REVIEW_YOUR_POLICY (step index 1)
      dispatch(setRenewalFlowType({ type: RENEW_POLICY_LOGGED_IN, currentStepIndex: 1, currentSubstepIndex: 0 }));

      navigate("/renew/renew-motor-policy", { state: { motorFlow: { type: RENEW_POLICY_LOGGED_IN, currentStepIndex: 1, currentSubstepIndex: 0 } } });
    } catch (error) {
      console.error("Motor renewal handoff failed:", error);
      if (isMountedRef.current) {
        try {
          toast.error(translate(error.ErrorMessageEn, error.ErrorMessageAr));
        } catch {
          toast.error(translate("Could not start motor renewal.", "لم نتمكن من بدء تجديد التأمين."));
        }
        setIsLoading(false);
      }
    }
  };

  // Handle non-motor policy selection - just navigate to placeholder
  const handleNonMotorPolicySelection = (policy) => {
    const route = POLICY_TYPE_ROUTES[policy.ProductCode];
    if (route) {
      navigate(route, { state: { selectedPolicy: policy } });
    }
  };

  const continueClickHandler = async () => {
    if (!selected) {
      return;
    }

    // Motor insurance (product code 0101)
    if (selected.ProductCode === "0101") {
      await handleMotorPolicySelection(selected);
    } else {
      // Non-motor policies - navigate to placeholder
      handleNonMotorPolicySelection(selected);
    }
  };

  return (
    <RenewalLayout
      is_hide_back_btn={false}
      show_close_btn
      isFirstStep={true}
      btn_onClick={continueClickHandler}
      btn_disabled={!selected || !renewablePolicies || renewablePolicies.length === 0 || isLoading}
    >
      <div className="selectpolicy p-4 w-full max-w-[500px] mx-auto max767:px-5 max767:pt-0">
        <TitleDescription
          title={translate("Renew Your Policy", "تجديد السياسة")}
          description={
            screenResolution.mobile
              ? translate("Select Your Policy to renew", "اختر وثيقتك للتجديد")
              : translate(
                  "Select the policy you would like to renew from the list below.",
                  "يرجى اختيار السياسة التي ترغب في تجديدها."
                )
          }
          className="mb-6"
        />
        <div className="">
          <div className="bg-gray-100 p-4 rounded-[26px] max767:bg-white max767:p-0">
            {!screenResolution.mobile && (
              <p className="mb-4 text-center text-[#48535B] text-base not-italic font-medium leading-[26px] tracking-[-0.16px] max767:text-left">
                {translate(
                  "Select Your Policy to renew",
                  "اختر سياسة للتجديد"
                )}
              </p>
            )}
            <div className="grid justify-center gap-4 policycardbox">
              {(() => {
                const isFetching = fetchPoliciesLoading || (!userPolicies && !hasAttemptedFetch);
                if (isFetching) {
                  return <SkeletonLoader />;
                }
                if (!renewablePolicies || renewablePolicies.length === 0) {
                  return (
                    <p className="text-center">
                      {translate(
                        "You have no policies due for renewal",
                        "ليس لديك وثائق مستحقة للتجديد"
                      )}
                    </p>
                  );
                }

                return renewablePolicies.map((each, index) => (
                  <PolicyCard
                    key={index}
                    policy_name={each.policy_name}
                    policy_label={each.policy_label}
                    policy_number={each.policy_number}
                    from_date={each.from_date}
                    to_date={each.to_date}
                    policy_bg_image={each.policy_bg_image}
                    expire_days={each.expire_days}
                    is_text_dark={each.is_text_dark}
                    mapping_key={each.id}
                    data={each}
                    showSelectButton
                    isSelected={selected?.PolicyNumber === each?.PolicyNumber}
                    selectClickHandler={() => { if (!isLoading) selectClickHandler(each); }}
                  />
                ));
              })()}
            </div>
          </div>
        </div>
      </div>
    </RenewalLayout>
  );
};

export default GenericSelectPoliciesPage;
